import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { cleanTask } from "@/lib/tasks";
import TaskBuilder from "@/components/jobs/TaskBuilder";

const toEdit = (t) => ({ id: t.id, title: t.title, description: t.notes || "", role: t.role || "", assignee_id: t.user_id, duration_minutes: t.duration_minutes,
  sets_status: t.sets_status || "", subtasks: t.subtasks || [] });

function Option({ checked, onChange, label, hint, type = "radio", testId }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer text-sm text-[#103080]" data-testid={testId}>
      <input type={type} checked={checked} onChange={onChange} className="mt-1 accent-[#103080]" />
      <span><b className="font-semibold">{label}</b><span className="block text-xs text-[#0F172A]/55">{hint}</span></span>
    </label>
  );
}

export default function CardEditor({ card, isAdmin, directory, roleNames, statuses, defaultAssignee, onCancel, onSaved }) {
  const [tasks, setTasks] = useState(card.tasks.map(toEdit));
  const [future, setFuture] = useState(false);
  const [tpl, setTpl] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!tasks.length) return toast.error("Add at least one task");
    setBusy(true);
    try {
      await api.put(`/cards/${card.id}/tasks`, { tasks: tasks.map(cleanTask), apply_future: future, update_template: tpl });
      toast.success(future ? "Saved to this job card and future ones for this client" : "Job card updated");
      if (tpl) toast.success("Template updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="card-editor">
      <TaskBuilder tasks={tasks} onChange={setTasks} roleNames={roleNames} statuses={statuses} directory={directory} defaultAssignee={defaultAssignee} testPrefix="card-edit" />
      <div className="rounded-xl bg-[#F5F2EB] p-4 grid md:grid-cols-3 gap-4">
        <Option checked={!future} onChange={() => setFuture(false)} label="Just this job card" hint="Only this period changes." testId="card-edit-scope-this" />
        <Option checked={future} onChange={() => setFuture(true)} label="This and future job cards" hint={`Rolls forward to every future ${card.title} for ${card.client_name}.`} testId="card-edit-scope-future" />
        {isAdmin && card.template_id && (
          <Option type="checkbox" checked={tpl} onChange={() => setTpl(!tpl)} label="Also update the template" hint="New clients onboarded with this template get these tasks." testId="card-edit-update-template" />
        )}
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="card-edit-cancel" className="rounded-full border border-[#103080]/20 px-6 h-11 text-sm font-medium text-[#103080] hover:border-[#0090D0] transition-colors duration-200">Cancel</button>
        <button type="submit" disabled={busy} data-testid="card-edit-save" className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200 disabled:opacity-60">Save job card</button>
      </div>
    </form>
  );
}
